import React, { useState } from "react";
import useUpdate from "../../../../../api/useUpdate";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import BarcodeScanner from "../../../../../Components/BarcodeScanner/BarcodeScanner";
import MedicineLabelPreview from "../../../../../Components/BarcodeScanner/MedicineLabelPreview";
import { printMedicineLabel } from "../../../../../Components/BarcodeScanner/printMedicineLabel";
import { playScanBeep } from "../../../../../Components/BarcodeScanner/playScanBeep";

const EditMedicine = ({ open, onClose, medicine, categoryData, token }) => {
  const { t } = useTranslation();
  const [name, setName] = useState(medicine?.name || "");
  const [nameError, setNameError] = useState("");
  const [category, setCategory] = useState(
    (categoryData || []).find((cat) => cat.id === medicine.category) || null,
  );
  const [description, setDescription] = useState(medicine?.description || "");
  const [barcode, setBarcode] = useState(medicine?.barcode || "");

  const { handleUpdate } = useUpdate("medicines", token, null);

  const handleSubmit = (e) => {
    e.preventDefault();
    let isValid = true;

    if (!name) {
      setNameError(t("pharmacy.nameRequired"));
      isValid = false;
    } else {
      setNameError("");
    }

    if (isValid) {
      handleUpdate(medicine.id, {
        name,
        category: category?.id || null,
        description,
        barcode: barcode || "",
      });
      onClose();
    }
  };

  const handlePrint = async (mode = "barcode") => {
    if (!barcode) {
      toast.error(t("pharmacy.noBarcodeToPrint"));
      return;
    }
    const ok = await printMedicineLabel({
      barcode,
      name,
      category: category?.name,
      mode,
    });
    if (!ok) toast.error(t("pharmacy.printBlocked"));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      component="form"
      onSubmit={handleSubmit}
    >
      <DialogTitle>{t("pharmacy.updateMedicine")}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label={t("pharmacy.medicineName")}
          fullWidth
          variant="outlined"
          value={name}
          error={!!nameError}
          helperText={nameError}
          onChange={(e) => setName(e.target.value)}
        />
        <TextField
          margin="dense"
          label={t("pharmacy.barcode")}
          fullWidth
          variant="outlined"
          value={barcode}
          onChange={(e) => setBarcode(e.target.value)}
          helperText={t("pharmacy.barcodeOptionalHelp")}
        />
        <Box sx={{ mt: 1.5, mb: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            {t("pharmacy.scanOrTypeBarcode")}
          </Typography>
          <BarcodeScanner
            listenHardware={false}
            onScan={(code) => {
              playScanBeep(true);
              setBarcode(code);
              toast.success(t("pharmacy.barcodeCaptured", { barcode: code }));
            }}
          />
        </Box>
        {barcode && (
          <Box sx={{ mb: 2 }}>
            <MedicineLabelPreview
              barcode={barcode}
              name={name}
              category={category?.name}
            />
            <Box display="flex" gap={1} mt={1} flexWrap="wrap">
              <Button size="small" variant="contained" onClick={() => handlePrint("barcode")}>
                {t("pharmacy.printBarcode")}
              </Button>
              <Button size="small" variant="outlined" onClick={() => handlePrint("qr")}>
                {t("pharmacy.printQrCode")}
              </Button>
            </Box>
          </Box>
        )}
        <Autocomplete
          options={categoryData || []}
          getOptionLabel={(option) => option.name}
          value={category || null}
          onChange={(event, newValue) => {
            if (newValue) {
              setCategory(newValue);
            } else {
              setCategory(null);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              margin="dense"
              label={t("pharmacy.category")}
              variant="outlined"
              helperText={t("pharmacy.categoryOptionalHelp")}
            />
          )}
          fullWidth
        />

        <TextField
          margin="dense"
          label={t("pharmacy.description")}
          fullWidth
          multiline
          rows={4}
          value={description}
          helperText={t("pharmacy.descriptionOptionalHelp")}
          onChange={(e) => setDescription(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" color="warning">
          {t("pharmacy.cancel")}
        </Button>
        <Button
          variant="contained"
          color="success"
          type="submit"
          sx={{ margin: "0px 10px" }}
        >
          {t("pharmacy.update")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditMedicine;
